import { useMemo } from 'react';
import { FileUploadMetaDataExtractionDto } from '@asset-metrix/portal-gateway-client-js';
import { Options } from '@asset-metrix/portal-ui';
import { JsonSchema } from '@sagold/react-json-editor';

export type GenerateSchemaFromMetaDataExtraction = {
  inputLabel?: string;
  dropDownOptions?: Options;
  jsonSchema?: JsonSchema;
};

export const useGenerateSchemaForMetaDataExtraction = (
  jsonSchema: JsonSchema,
  data: { [p: string]: Options } | undefined,
  documentMetaDataExtraction?: FileUploadMetaDataExtractionDto
): GenerateSchemaFromMetaDataExtraction => {
  return useMemo(() => {
    if (
      !documentMetaDataExtraction?.enabled ||
      !documentMetaDataExtraction?.key
    ) {
      return {};
    }

    const clonedSchema = structuredClone(jsonSchema);
    const keyToRetain = documentMetaDataExtraction.key;
    const propertyToModify = clonedSchema.properties?.[keyToRetain];

    // if there is any error in the poco configuration of a doc-view with metadata extraction enabled.
    // then throw error manually.
    if (
      !propertyToModify ||
      typeof propertyToModify !== 'object' ||
      !propertyToModify.conditionalFilter ||
      !data ||
      !data[propertyToModify.conditionalFilter.dataProperty]
    ) {
      return {};
    }

    const dropDownOptions =
      data[propertyToModify.conditionalFilter.dataProperty];

    // Update property options with dropdown labels
    propertyToModify.options = {
      ...propertyToModify.options,
      showTitle: false,
      enum: dropDownOptions.labels,
    };

    // Update items schema with dropdown values
    const itemsSchema = propertyToModify.items as JsonSchema;
    itemsSchema.enum = dropDownOptions.values;
    itemsSchema.options = propertyToModify.options;

    return {
      inputLabel: propertyToModify.title,
      dropDownOptions,
      jsonSchema: {
        type: clonedSchema.type,
        required: [keyToRetain],
        properties: { [keyToRetain]: propertyToModify },
      },
    };
  }, [
    documentMetaDataExtraction?.enabled,
    documentMetaDataExtraction?.key,
    data,
    jsonSchema,
  ]);
};
